import ChevronRight from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import MaterialTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox.tsx';
import { Placeholder } from '@openx/components-core/lib/Placeholder/Placeholder.tsx';
import { SelectOperation, type ToggleSelect } from '@openx/types/selectableTable.ts';
import type { ChangeSingleCriteriaAction, CriteriaDimension, SortCriteria } from '@openx/types/tableCriteria.ts';
import type {
  BaseRow,
  Columns,
  RowAction,
  RowActionButtonBaseProps,
  RowActionProps,
  TableColumn,
} from '@openx/types/tableHelpers.ts';
import { STICKY_POSITION } from '@openx/types/tableHelpers.ts';
import {
  type ComponentType,
  Fragment,
  type JSX,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { HighlightRules } from '../../highlightRules.ts';
import { allItemsSelected, anyItemSelected } from '../../SelectableTable/selectCheckers.ts';
import { TableTileViewGrid } from '../../TileViewGrid/TableTileViewGrid.tsx';
import type { TileViewLabel } from '../../TileViewGrid/TileViewHeadLabels/TileViewHeadLabels.tsx';
import { ActionsCell } from '../ActionsCell.tsx';
import { ColumnFilter } from '../ColumnFilter.tsx';
import { CustomTableRow } from '../CustomTableRow.tsx';
import { TableHeader } from '../TableHeader.tsx';
import { TableRow, type TableRowPropsWithData } from '../TableRow.tsx';
import { TableRowTree } from '../TableRowTree.tsx';
import { ACTION_ICON_CELL, type CustomColumnConfig } from './config.ts';
import { calculateStickyRightOffset, groupData, loadColumnsConfig, normalizeColumnWidths } from './utils.ts';

function renderRowsSkeleton<RowT extends BaseRow>(columns: Columns<RowT>, rowsAmount = 3): JSX.Element[] {
  const result: JSX.Element[] = [];

  for (let i = 0; i < rowsAmount; i += 1) {
    result.push(<TableRow columns={columns} key={i} />);
  }

  return result;
}

const bookmarkRowTableCellSx = (theme: Theme) => ({
  backgroundColor: theme.palette.background.boxLight,
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontWeight: 'fontWeightBold',
  padding: `${2} 0`,
});

const GROUP_SELECT_COLUMN_WIDTH = 56;
const GROUP_STICKY_LEFT_Z_INDEX = 5;
const GROUP_STICKY_MIDDLE_Z_INDEX = 6;
const GROUP_STICKY_RIGHT_Z_INDEX = 7;
const GROUP_STICKY_ACTION_COLUMN_WIDTH = 68;
const GROUP_ACTIONS_COLSPAN = 1;

export interface NoDataComponentProps<RowT extends BaseRow> {
  headerColumns: (TableColumn<RowT> | { key: string; title: ReactNode })[];
  onSortChange: ChangeSingleCriteriaAction<typeof CriteriaDimension.SORT>;
  sortCriteria?: SortCriteria;
  stickyTable?: boolean;
  overrideTableCellStyles?: SxProps<Theme>;
}

export interface DataSectionProps<RowT extends BaseRow> {
  name?: string;
  data: RowT[];
  columns: Columns<RowT>;
  onRowClick?: RowAction<RowT>;
  loading: boolean;
  noItemsLabel?: (data: RowT) => string;
  isTree?: boolean;
  fetchTreeChildren?: (data: RowT) => Promise<RowT[]>;
  isForcedExpanded?: (data: RowT) => boolean;
  highlightRules?: HighlightRules;
  sortCriteria?: SortCriteria;
  onSortChange: ChangeSingleCriteriaAction<typeof CriteriaDimension.SORT>;
  primaryRowAction?: RowActionButtonBaseProps<RowT>;
  secondaryRowAction?: RowActionButtonBaseProps<RowT>;
  dangerRowAction?: RowActionButtonBaseProps<RowT>;
  defaultRowAction?: RowActionButtonBaseProps<RowT>;
  customRowActions?: RowActionButtonBaseProps<RowT>[];
  rowActions?: RowActionProps<RowT>[];
  treeLevelItems?: number;
  stickyTable?: boolean;
  enableTableHeaderCustomization: boolean;
  renderCustomRow?: (rowProps: TableRowPropsWithData<RowT>, isForcedExpanded?: (data: RowT) => boolean) => ReactElement;
  overrideTableCellStyles?: SxProps<Theme>;
  groupBy?: keyof RowT;
  toggleSelect?: ToggleSelect<RowT>;
  selectedItems?: RowT[];
  forceExpandGroupedItems?: boolean;
  groupedItemsActions?: React.ReactNode;
  tileViewEnabled?: boolean;
  TileViewComponent?: ComponentType<{ item: RowT }>;
  TileViewLoadingComponent?: ComponentType;
  tileViewHeaderLabels?: TileViewLabel[];
  'data-test'?: string;
  getRowKey?: (row: RowT, index?: number) => number | string;
  skeletonRows?: number;
  customNoDataComponent?: ComponentType<NoDataComponentProps<RowT>>;
  stickyActionColumn?: boolean;
}

export function DataSection<RowT extends BaseRow>(props: DataSectionProps<RowT>): JSX.Element {
  const {
    name,
    noItemsLabel,
    isTree,
    fetchTreeChildren,
    isForcedExpanded,
    loading,
    data,
    onRowClick,
    primaryRowAction,
    secondaryRowAction,
    dangerRowAction,
    defaultRowAction,
    customRowActions,
    rowActions,
    treeLevelItems,
    stickyTable,
    enableTableHeaderCustomization,
    onSortChange,
    highlightRules,
    sortCriteria,
    renderCustomRow,
    overrideTableCellStyles,
    groupBy,
    toggleSelect,
    selectedItems = [],
    forceExpandGroupedItems,
    groupedItemsActions,
    tileViewEnabled,
    TileViewComponent,
    TileViewLoadingComponent,
    tileViewHeaderLabels,
    getRowKey,
    skeletonRows,
    customNoDataComponent,
    stickyActionColumn,
  } = props;

  const [columns, setColumns] = useState<Columns<RowT>>(props.columns);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    const loadedColumns = loadColumnsConfig(props.columns, name);
    const normalizedColumns = normalizeColumnWidths(loadedColumns);
    setColumns(normalizedColumns);
  }, [name, props.columns]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check scrollability when loading state or columns change, as table dimensions depend on rendered content
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number;

    const checkScrollable = () => {
      rafId = requestAnimationFrame(() => {
        const table = container.querySelector('table');
        const tableWidth = table?.scrollWidth ?? 0;
        const containerWidth = container.clientWidth;
        setIsScrollable(container.scrollWidth > containerWidth || tableWidth > containerWidth);
      });
    };

    const observer = new ResizeObserver(checkScrollable);
    observer.observe(container);
    const table = container.querySelector('table');
    if (table) observer.observe(table);

    checkScrollable();

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [loading, columns]);

  const groupedData = useMemo(() => groupData(data, groupBy), [data, groupBy]);

  const handleGroupToggle = useCallback((groupKey: string) => {
    setExpandedGroups(prevState => ({
      ...prevState,
      [groupKey]: !prevState[groupKey],
    }));
  }, []);

  useEffect(() => {
    if (forceExpandGroupedItems && groupedData) {
      setExpandedGroups(
        Object.keys(groupedData).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
    }
  }, [forceExpandGroupedItems, groupedData]);

  const filteredColumns = useMemo(() => {
    return columns.filter(singleColumn => singleColumn.hide !== true);
  }, [columns]);

  const stickyActionColumnOffset = useMemo(() => {
    if (!stickyActionColumn) return 0;
    return calculateStickyRightOffset(filteredColumns);
  }, [filteredColumns, stickyActionColumn]);

  const hasOtherStickyRightColumns = useMemo(() => {
    return filteredColumns.some(col => col.sticky === STICKY_POSITION.RIGHT);
  }, [filteredColumns]);

  const iconCellRenderer = useMemo(() => {
    if (
      rowActions ||
      primaryRowAction ||
      secondaryRowAction ||
      dangerRowAction ||
      defaultRowAction ||
      customRowActions
    ) {
      return (singleRowData: RowT) => (
        <ActionsCell
          rowData={singleRowData}
          actions={rowActions}
          primaryAction={primaryRowAction}
          secondaryAction={secondaryRowAction}
          dangerAction={dangerRowAction}
          defaultAction={defaultRowAction}
          customRowActions={customRowActions}
          sticky={stickyActionColumn ? STICKY_POSITION.RIGHT : undefined}
          stickyRightOffset={stickyActionColumn ? 0 : undefined}
          isLeadingStickyRight={stickyActionColumn && !hasOtherStickyRightColumns}
        />
      );
    }

    const placeholder = enableTableHeaderCustomization ? () => <TableCell /> : undefined;

    return placeholder;
  }, [
    dangerRowAction,
    defaultRowAction,
    customRowActions,
    enableTableHeaderCustomization,
    primaryRowAction,
    rowActions,
    secondaryRowAction,
    stickyActionColumn,
    hasOtherStickyRightColumns,
  ]);

  const updateLocalStorage = useCallback(
    (cols: Columns<RowT>) => {
      const config: CustomColumnConfig[] = cols.map((column, index) => {
        return { hide: !!column.hide, key: column.key, index };
      });

      name && localStorage.setItem(`tableHeaderConfig-${name}`, JSON.stringify(config));
    },
    [name]
  );

  const handleFilterColumn = useCallback(
    (column: TableColumn<RowT>) => {
      const updatedColumns = columns.map(singleColumn => {
        if (singleColumn.key === column.key) {
          return { ...singleColumn, hide: !singleColumn.hide };
        }
        return singleColumn;
      });

      const normalizedColumns = normalizeColumnWidths(updatedColumns, props.columns);
      setColumns(normalizedColumns);
      updateLocalStorage(normalizedColumns);
    },
    [columns, props.columns, updateLocalStorage]
  );

  const filterColumnNode: ReactNode = useMemo(
    () => (
      <ColumnFilter
        columns={columns}
        originalColumns={props.columns}
        handleFilterColumn={handleFilterColumn}
        updateLocalStorage={updateLocalStorage}
        updateColumns={setColumns}
      />
    ),
    [columns, handleFilterColumn, props.columns, updateLocalStorage]
  );

  const headerColumns = useMemo(() => {
    const icon: ReactNode = filterColumnNode;
    const actionCell = enableTableHeaderCustomization
      ? [
          {
            key: ACTION_ICON_CELL,
            title: icon,
            sticky: stickyActionColumn ? STICKY_POSITION.RIGHT : undefined,
            width: '68px',
            minWidth: '68px',
          },
        ]
      : [];

    return [...filteredColumns, ...actionCell];
  }, [filteredColumns, filterColumnNode, enableTableHeaderCustomization, stickyActionColumn]);

  const getRowComponent = useCallback(
    (row: RowT, index: number, _: RowT[], inBookmark?: boolean): JSX.Element => {
      const rowProps = {
        columns: filteredColumns,
        dangerRowAction,
        defaultRowAction,
        highlightRules,
        iconCellRenderer,
        key: getRowKey?.(row, index) ?? row.id ?? index,
        noItemsLabel,
        onRowClick,
        overrideTableCellStyles,
        primaryRowAction,
        rowActions,
        rowData: row,
        secondaryRowAction,
        treeLevelItems,
      };

      if (isTree) {
        return (
          <TableRowTree
            {...rowProps}
            stickyActionColumn={stickyActionColumn}
            key={`${rowProps.key}|${rowProps.rowData?.children?.length || 0}`}
            isForcedExpanded={isForcedExpanded}
            fetchTreeChildren={fetchTreeChildren}
          />
        );
      }

      if (renderCustomRow) {
        return (
          <CustomTableRow
            key={rowProps.key}
            isForcedExpanded={isForcedExpanded}
            renderCustomRow={renderCustomRow}
            rowProps={rowProps}
          />
        );
      }

      const { key, ...tableProps } = rowProps;

      return (
        <TableRow
          {...tableProps}
          stickyActionColumn={stickyActionColumn}
          sx={
            inBookmark
              ? {
                  [`& > ${columns[0]?.key === 'select' ? ':nth-of-type(2)' : ':first-child'}`]: {
                    paddingLeft: '44px',
                  },
                  backgroundColor: theme => theme.palette.background.boxLight,
                }
              : undefined
          }
          key={key}
        />
      );
    },
    [
      filteredColumns,
      dangerRowAction,
      defaultRowAction,
      noItemsLabel,
      highlightRules,
      iconCellRenderer,
      onRowClick,
      primaryRowAction,
      rowActions,
      secondaryRowAction,
      stickyActionColumn,
      treeLevelItems,
      isTree,
      isForcedExpanded,
      fetchTreeChildren,
      renderCustomRow,
      overrideTableCellStyles,
      columns,
      getRowKey,
    ]
  );

  const renderGroup = useCallback(
    (groupKey: string, rows: RowT[]) => {
      const isExpanded = expandedGroups[groupKey];
      const hasSelectColumn = columns[0]?.key === 'select';
      const hasGroupedItemsActions = Boolean(groupedItemsActions);
      const groupedBookmarkLeftOffset = hasSelectColumn ? GROUP_SELECT_COLUMN_WIDTH : 0;

      return (
        <Fragment key={`group-${groupKey}`}>
          <tr>
            {hasSelectColumn && (
              <TableCell
                sx={theme => ({
                  ...bookmarkRowTableCellSx(theme),
                  paddingLeft: 4,
                  ...(stickyActionColumn
                    ? {
                        position: 'sticky',
                        left: 0,
                        zIndex: GROUP_STICKY_LEFT_Z_INDEX,
                      }
                    : undefined),
                })}
              >
                <Checkbox
                  checked={anyItemSelected(rows, selectedItems)}
                  data-test="group-checkbox"
                  indeterminate={anyItemSelected(rows, selectedItems) && !allItemsSelected(rows, selectedItems)}
                  onClick={e => e.stopPropagation()}
                  onChange={({ target: { checked } }) => {
                    toggleSelect?.(rows, checked ? SelectOperation.SELECT : SelectOperation.UNSELECT);
                  }}
                  color="primary"
                />
              </TableCell>
            )}

            <TableCell
              colSpan={hasGroupedItemsActions ? filteredColumns.length - GROUP_ACTIONS_COLSPAN : filteredColumns.length}
              sx={theme => ({
                ...bookmarkRowTableCellSx(theme),
                ...(hasGroupedItemsActions
                  ? {
                      paddingRight: `${GROUP_STICKY_ACTION_COLUMN_WIDTH}px`,
                    }
                  : undefined),
              })}
              data-test="group-bookmark"
            >
              <Box
                data-test="group-bookmark-content"
                sx={theme => ({
                  alignItems: 'center',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  ...(stickyActionColumn
                    ? {
                        backgroundColor: theme.palette.background.boxLight,
                        left: groupedBookmarkLeftOffset,
                        maxWidth: hasGroupedItemsActions
                          ? `calc(100% - ${GROUP_STICKY_ACTION_COLUMN_WIDTH}px)`
                          : '100%',
                        overflow: 'hidden',
                        position: 'sticky',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        zIndex: GROUP_STICKY_MIDDLE_Z_INDEX,
                      }
                    : undefined),
                })}
                onClick={() => handleGroupToggle(groupKey)}
              >
                <Box component="span">
                  <IconButton
                    sx={{
                      marginRight: 2,
                    }}
                    key="arrowExpand"
                    aria-label="arrowExpand"
                    data-test="expand-icon"
                    data-expandicon="expandRow"
                    size="small"
                  >
                    <ChevronRight
                      sx={{
                        transform: isExpanded ? 'rotate(90deg)' : '',
                      }}
                      data-expandicon="expandRow"
                      color="primary"
                    />
                  </IconButton>{' '}
                  {groupKey}
                </Box>
              </Box>
            </TableCell>

            {hasGroupedItemsActions && (
              <TableCell
                colSpan={GROUP_ACTIONS_COLSPAN}
                sx={theme => ({
                  ...bookmarkRowTableCellSx(theme),
                  textAlign: 'right',
                  ...(stickyActionColumn
                    ? {
                        maxWidth: GROUP_STICKY_ACTION_COLUMN_WIDTH,
                        minWidth: GROUP_STICKY_ACTION_COLUMN_WIDTH,
                        position: 'sticky',
                        right: 0,
                        zIndex: GROUP_STICKY_RIGHT_Z_INDEX,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        overflow: 'visible',
                        paddingRight: 0,
                        whiteSpace: 'nowrap',
                        width: GROUP_STICKY_ACTION_COLUMN_WIDTH,
                      }
                    : undefined),
                })}
              >
                <Box
                  sx={
                    stickyActionColumn
                      ? {
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          whiteSpace: 'nowrap',
                        }
                      : { display: 'inline-flex', whiteSpace: 'nowrap' }
                  }
                >
                  {groupedItemsActions}
                </Box>
              </TableCell>
            )}
          </tr>

          {isExpanded && rows.map((row, index) => getRowComponent(row, index, rows, true))}
        </Fragment>
      );
    },
    [
      expandedGroups,
      toggleSelect,
      columns,
      selectedItems,
      filteredColumns.length,
      handleGroupToggle,
      getRowComponent,
      groupedItemsActions,
      stickyActionColumn,
    ]
  );

  const rows = useMemo(() => {
    if (groupBy) {
      return Object.entries(groupedData).map(([groupKey, rows]) => renderGroup(groupKey, rows));
    }

    return data.map(getRowComponent);
  }, [data, getRowComponent, groupBy, groupedData, renderGroup]);

  const resultNotFound = !loading && data.length === 0;

  if (resultNotFound) {
    if (customNoDataComponent) {
      const NoDataComponent = customNoDataComponent;
      return (
        <NoDataComponent
          headerColumns={headerColumns}
          onSortChange={onSortChange}
          sortCriteria={sortCriteria}
          stickyTable={stickyTable}
          overrideTableCellStyles={overrideTableCellStyles}
        />
      );
    }

    return (
      <Placeholder
        title={t('No results found')}
        subtitle={t('Please try different criteria in order to find matching results')}
      />
    );
  }

  const tableBody = loading ? renderRowsSkeleton(headerColumns, skeletonRows) : rows;

  if (tileViewEnabled && TileViewComponent) {
    return (
      <TableTileViewGrid
        isLoading={loading}
        data={data}
        TileViewComponent={TileViewComponent}
        TileViewLoadingComponent={TileViewLoadingComponent}
        headerLabels={tileViewHeaderLabels}
        customTileActions={customRowActions}
      />
    );
  }

  return (
    <TableContainer ref={containerRef} data-scrollable={isScrollable}>
      <MaterialTable sx={{ width: '100%', minWidth: '100%' }}>
        <TableHeader
          onChange={onSortChange}
          columns={headerColumns}
          criteria={sortCriteria}
          stickyTable={stickyTable}
          overrideTableCellStyles={overrideTableCellStyles}
          stickyActionColumnOffset={stickyActionColumn ? stickyActionColumnOffset : undefined}
        />

        <TableBody data-test={loading ? 'table-body-loading' : 'table-body'}>{tableBody}</TableBody>
      </MaterialTable>
    </TableContainer>
  );
}
